import type { SpaceAdapterRuntimeController } from '@toolproof-core/visualization/adapter/_contract/runtime';
import type { Config, CosmosDataModel, RuntimeState } from '@/adapters/biology/_contract/config';
import { BIOLOGY_STRUCTURES, DEFAULT_STRUCTURE_ID, biologyStructureEntry, type BiologyStructureId } from '@/adapters/biology/_data/structures';
import { activeProteinStructure } from '@/adapters/biology/_pure/activeStructure';

export const runtimeController: SpaceAdapterRuntimeController<Config, RuntimeState, CosmosDataModel> = {
    createInitialRuntimeState: () => ({ structureId: DEFAULT_STRUCTURE_ID, view: 'trace', selection: null }),
    buildOverlayModel: ({ runtimeState, cosmosData }) => {
        const structure = activeProteinStructure(runtimeState, cosmosData);
        const selection = runtimeState.selection;
        const selectedAtom = selection?.kind === 'atom'
            ? structure?.atoms.find((atom) => atom.id === selection.atomId)
            : undefined;
        const selectedResidue = selection?.kind === 'residue'
            ? structure?.chains.find((chain) => chain.id === selection.chainId)?.residues
                .find((residue) => residue.sequence === selection.sequence)
            : selectedAtom
                ? structure?.chains.find((chain) => chain.id === selectedAtom.chainId)?.residues
                    .find((residue) => residue.sequence === selectedAtom.residueSequence)
                : undefined;
        const selectedChainId = selection?.kind === 'residue' ? selection.chainId : selectedAtom?.chainId;
        const selectedSequence = selection?.kind === 'residue' ? selection.sequence : selectedAtom?.residueSequence;
        const selectedResidueAtoms = selectedChainId === undefined || selectedSequence === undefined
            ? []
            : structure?.atoms.filter((atom) =>
                atom.chainId === selectedChainId && atom.residueSequence === selectedSequence,
            ) ?? [];
        const selectedAtomIds = new Set(selectedResidueAtoms.map((atom) => atom.id));
        const neighborAtomIds = new Set<string>();
        for (const bond of structure?.bonds ?? []) {
            if (selectedAtomIds.has(bond.atomIdA)) neighborAtomIds.add(bond.atomIdB);
            if (selectedAtomIds.has(bond.atomIdB)) neighborAtomIds.add(bond.atomIdA);
        }
        for (const atomId of selectedAtomIds) neighborAtomIds.delete(atomId);
        const selectedName = selectedAtom
            ? `${selectedAtom.name} (${selectedAtom.element})`
            : selectedResidue ? `${selectedResidue.name} ${selectedResidue.sequence}` : undefined;
        const detailRows = selection && selectedResidue
            ? [
                { key: 'Selection', value: selectedName ?? 'Residue' },
                { key: 'Chain', value: selectedChainId ?? '—' },
                { key: 'Residue', value: `${selectedResidue.name} ${selectedResidue.sequence}` },
                ...(selectedAtom ? [{ key: 'Element', value: selectedAtom.element }] : []),
                { key: 'Atoms in residue', value: String(selectedResidueAtoms.length) },
                { key: selectedAtom ? 'Bonded atoms' : 'Adjacent atoms outside residue', value: String(neighborAtomIds.size) },
                { key: 'Secondary structure', value: selectedResidue.secondaryStructure },
            ]
            : undefined;
        const load = runtimeState.structureLoad?.id === runtimeState.structureId ? runtimeState.structureLoad : undefined;
        const loadedRuntime = runtimeState.loadedStructure?.structure.id === structure?.id
            ? runtimeState.loadedStructure : undefined;
        const structureRows = structure
            ? [
                { key: 'Shown', value: structure.id },
                ...(load ? [{
                    key: 'Status',
                    value: load.status === 'loading' ? `Loading ${load.id}…` : `${load.id} failed: ${load.message}`,
                }] : []),
                { key: 'Chains', value: String(structure.chains.length) },
                { key: 'Residues', value: String(structure.chains.reduce((sum, chain) => sum + chain.residues.length, 0)) },
                { key: 'Atoms', value: String(structure.atoms.length) },
                { key: 'Bonds', value: String(structure.bonds.length) },
                ...(loadedRuntime ? [{ key: 'Fetch + parse', value: `${loadedRuntime.loadMilliseconds} ms` }] : []),
            ]
            : [];
        return {
            overlayId: 'biology',
            title: 'Protein',
            subtitle: selection && !selectedResidue
                ? `${structure?.id ?? ''} · Selection is not present in this structure`
                : selectedName
                    ? `${structure?.id ?? ''} · ${selectedName}`
                    : `${structure?.id ?? ''} · Recorded protein atoms; no added hydrogens`,
            sections: [
                {
                    sectionKind: 'select',
                    sectionId: 'protein-structure',
                    label: 'Structure',
                    selectedValue: runtimeState.structureId,
                    options: BIOLOGY_STRUCTURES.map(({ id, label }) => ({ value: id, label })),
                },
                {
                    sectionKind: 'select',
                    sectionId: 'protein-view',
                    label: 'Representation',
                    selectedValue: runtimeState.view,
                    options: [
                        { value: 'trace', label: 'Secondary-structure trace' },
                        { value: 'ball-and-stick', label: 'Atoms and bonds' },
                    ],
                },
                ...(structureRows.length > 0 ? [{
                    sectionKind: 'details' as const,
                    sectionId: 'protein-structure-size',
                    label: 'Structure size',
                    rows: structureRows,
                }] : []),
                ...(detailRows ? [{
                    sectionKind: 'details' as const,
                    sectionId: 'protein-selection',
                    label: 'Selection details',
                    rows: detailRows,
                }] : []),
            ],
        };
    },
    reduceOverlayIntent: ({ runtimeState, intent }) => {
        if (intent.overlayIntentKind !== 'select-changed') return runtimeState;
        if (intent.sectionId === 'protein-structure') {
            const entry = biologyStructureEntry(intent.value);
            if (!entry) throw new Error(`Unknown protein structure: ${intent.value}`);
            if (entry.id === runtimeState.structureId) return runtimeState;
            return { ...runtimeState, structureId: entry.id as BiologyStructureId, structureLoad: undefined, selection: null };
        }
        if (intent.sectionId !== 'protein-view') return runtimeState;
        if (intent.value !== 'trace' && intent.value !== 'ball-and-stick') {
            throw new Error(`Unknown protein representation: ${intent.value}`);
        }
        return { ...runtimeState, view: intent.value };
    },
};
